import { useState, useRef, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useExpireHolds, useSendTestEmail, useEmailStatus, usePreviewEmail, useServiceApiKeyStatus, useGenerateServiceApiKey, useRevokeServiceApiKey } from '@/hooks/useAdminTools';
import { useSearchUsers } from '@/hooks/useUsers';
import { CheckCircle, AlertCircle, Clock, Mail, Send, Eye, Key, Copy, Check } from 'lucide-react';

export default function AdminTools() {
  const expireHoldsMutation = useExpireHolds();
  const sendTestEmailMutation = useSendTestEmail();
  const previewEmailMutation = usePreviewEmail();
  const { data: emailStatus, isLoading: emailStatusLoading } = useEmailStatus();
  const { data: users, isLoading: usersLoading } = useSearchUsers({ isActive: true });
  const { data: serviceApiKeyStatus, isLoading: serviceApiKeyStatusLoading, refetch: refetchServiceApiKeyStatus } = useServiceApiKeyStatus();
  const generateServiceApiKeyMutation = useGenerateServiceApiKey();
  const revokeServiceApiKeyMutation = useRevokeServiceApiKey();

  const [expireResult, setExpireResult] = useState<{
    success: boolean;
    message: string;
    data?: {
      expiredLineItems: number;
      timestamp: string;
    };
  } | null>(null);

  const [emailResult, setEmailResult] = useState<{
    success: boolean;
    message: string;
    data?: {
      messageId: string;
      recipient: string;
      timestamp: string;
    };
  } | null>(null);

  const [selectedUserId, setSelectedUserId] = useState('');
  const [emailSubject, setEmailSubject] = useState('Test Email from Holds Request App');
  const [emailMessage, setEmailMessage] = useState('This is a test email to verify that email delivery is working correctly.');

  const [previewHoldRequestId, setPreviewHoldRequestId] = useState('');
  const [previewNotificationType, setPreviewNotificationType] = useState<'hold_request_created' | 'hold_request_completed' | 'line_item_assigned' | 'line_item_status_changed' | 'hold_request_overdue' | 'exception_approval_required'>('hold_request_created');
  const [previewRecipientEmail, setPreviewRecipientEmail] = useState('');
  const [previewResult, setPreviewResult] = useState<{
    success: boolean;
    message: string;
    data?: { messageId: string; recipient: string; requestNumber: string; subject: string; timestamp: string; marketDLRouting?: { isChartingType: boolean; isLineItemLevel?: boolean; marketCodes: string[]; distributionListAddresses: string[]; note: string } };
  } | null>(null);

  const [generatedApiKey, setGeneratedApiKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleExpireHolds = () => {
    setExpireResult(null);
    expireHoldsMutation.mutate(undefined, {
      onSuccess: (response) => {
        setExpireResult({
          success: response.success,
          message: response.message,
          data: response.data,
        });
      },
      onError: (error: unknown) => {
        const err = error as Error & { response?: { data?: { error?: string } }; message?: string };
        setExpireResult({
          success: false,
          message: err?.response?.data?.error || err?.message || 'Failed to expire holds',
        });
      },
    });
  };

  const handleSendTestEmail = () => {
    if (!selectedUserId) return;
    
    setEmailResult(null);
    sendTestEmailMutation.mutate(
      { userId: selectedUserId, subject: emailSubject, message: emailMessage },
      {
        onSuccess: (response) => {
          setEmailResult({
            success: response.success,
            message: response.message || response.error || 'Email sent',
            data: response.data,
          });
        },
        onError: (error: unknown) => {
          const err = error as Error & { response?: { data?: { message?: string; error?: string } }; message?: string };
          setEmailResult({
            success: false,
            message: err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to send email',
          });
        },
      }
    );
  };

  const handlePreviewEmail = () => {
    if (!previewHoldRequestId || !previewRecipientEmail) return;

    setPreviewResult(null);
    previewEmailMutation.mutate(
      { holdRequestId: previewHoldRequestId, notificationType: previewNotificationType, recipientEmail: previewRecipientEmail },
      {
        onSuccess: (response) => {
          setPreviewResult({
            success: response.success,
            message: response.message || response.error || 'Email sent',
            data: response.data,
          });
        },
        onError: (error: unknown) => {
          const err = error as Error & { response?: { data?: { message?: string; error?: string } }; message?: string };
          setPreviewResult({
            success: false,
            message: err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to send preview email',
          });
        },
      }
    );
  };

  const handleGenerateServiceApiKey = () => {
    const isRegenerate = serviceApiKeyStatus?.configured;
    const confirmMsg = isRegenerate
      ? 'This invalidates the current key. OUTGPT must be updated with the new value or hold submissions will fail.'
      : 'Generate a new service API key for OUTGPT?';

    if (!window.confirm(confirmMsg)) return;

    setGeneratedApiKey(null);
    setCopied(false);
    generateServiceApiKeyMutation.mutate(undefined, {
      onSuccess: (response) => {
        setGeneratedApiKey(response.data.apiKey);
        refetchServiceApiKeyStatus();
      },
      onError: (error: unknown) => {
        const err = error as Error & { response?: { data?: { error?: string } }; message?: string };
        alert(err?.response?.data?.error || err?.message || 'Failed to generate service API key');
      },
    });
  };

  const handleRevokeServiceApiKey = () => {
    if (!window.confirm('Revoke the admin-managed service API key? OUTGPT will no longer authenticate with this key. (If a legacy SERVICE_API_KEY env fallback is configured on the server, that would still work until it is removed.)')) return;

    revokeServiceApiKeyMutation.mutate(undefined, {
      onSuccess: () => {
        setGeneratedApiKey(null);
        refetchServiceApiKeyStatus();
        alert('Admin-managed service API key revoked. (A legacy SERVICE_API_KEY env fallback, if configured, remains active until removed on the server.)');
      },
      onError: (error: unknown) => {
        const err = error as Error & { response?: { data?: { error?: string } }; message?: string };
        alert(err?.response?.data?.error || err?.message || 'Failed to revoke service API key');
      },
    });
  };

  const copyResetTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Clear a pending "Copied" reset on unmount so it never fires setState on an
  // unmounted component.
  useEffect(() => () => {
    if (copyResetTimeout.current) clearTimeout(copyResetTimeout.current);
  }, []);

  const handleCopyApiKey = async () => {
    if (!generatedApiKey) return;
    try {
      await navigator.clipboard.writeText(generatedApiKey);
      setCopied(true);
      if (copyResetTimeout.current) clearTimeout(copyResetTimeout.current);
      copyResetTimeout.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can fail (permission denied, non-secure context, API
      // unavailable). The key stays visible in the field for manual copy.
      alert('Copy failed. Select the key in the field and copy it manually.');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-neutral-900">Admin Tools</h1>

      {/* Email Test Tool */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="h-5 w-5 text-neutral-400" />
          <h2 className="text-lg font-semibold text-neutral-900">Test Email Delivery</h2>
        </div>

        {/* Email Status */}
        {emailStatusLoading ? (
          <div className="flex items-center gap-2 text-sm text-neutral-400 mb-4">
            <LoadingSpinner />
            <span>Checking email service status...</span>
          </div>
        ) : emailStatus ? (
          <div className={`text-sm mb-4 p-3 rounded-lg ${emailStatus.configured ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
            <p>
              <span className="font-medium">Status:</span> {emailStatus.configured ? 'Configured' : 'Not configured'}
            </p>
            <p>
              <span className="font-medium">From:</span> {emailStatus.fromEmail}
            </p>
            <p>
              <span className="font-medium">Region:</span> {emailStatus.region}
            </p>
            {emailStatus.bounceStats.totalBounces > 0 && (
              <p className="mt-1 text-yellow-600">
                <span className="font-medium">Bounces:</span> {emailStatus.bounceStats.totalBounces} total ({emailStatus.bounceStats.hardBounces} hard, {emailStatus.bounceStats.softBounces} soft)
              </p>
            )}
          </div>
        ) : null}

        <div className="space-y-4">
          {/* User Selection */}
          <div>
            <label htmlFor="user-select" className="block text-sm font-medium text-neutral-600 mb-1">
              Select Recipient
            </label>
            <select
              id="user-select"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 rounded-md  focus:outline-none focus:ring-outfront-purple focus:border-outfront-purple"
              disabled={usersLoading}
            >
              <option value="">-- Select a user --</option>
              {users?.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} ({user.email})
                </option>
              ))}
            </select>
          </div>

          {/* Subject */}
          <div>
            <label htmlFor="email-subject" className="block text-sm font-medium text-neutral-600 mb-1">
              Subject
            </label>
            <input
              id="email-subject"
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 rounded-md  focus:outline-none focus:ring-outfront-purple focus:border-outfront-purple"
              placeholder="Email subject"
            />
          </div>

          {/* Message */}
          <div>
            <label htmlFor="email-message" className="block text-sm font-medium text-neutral-600 mb-1">
              Message
            </label>
            <textarea
              id="email-message"
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-neutral-200 rounded-md  focus:outline-none focus:ring-outfront-purple focus:border-outfront-purple"
              placeholder="Enter your test message..."
            />
          </div>

          <Button
            onClick={handleSendTestEmail}
            disabled={sendTestEmailMutation.isPending || !selectedUserId || !emailSubject || !emailMessage}
          >
            {sendTestEmailMutation.isPending ? (
              <>
                <LoadingSpinner />
                <span className="ml-2">Sending...</span>
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Test Email
              </>
            )}
          </Button>
        </div>

        {/* Email Result Display */}
        {emailResult && (
          <div
            className={`mt-4 p-4 rounded-lg ${
              emailResult.success
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <div className="flex items-start gap-2">
              {emailResult.success ? (
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p
                  className={`text-sm font-medium ${
                    emailResult.success ? 'text-green-800' : 'text-red-800'
                  }`}
                >
                  {emailResult.message}
                </p>
                {emailResult.success && emailResult.data && (
                  <div className="mt-2 text-sm text-green-700">
                    <p>Recipient: {emailResult.data.recipient}</p>
                    <p className="text-xs text-green-600 mt-1">
                      Message ID: {emailResult.data.messageId}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Email Preview Tool — Send real notification emails using hold request data */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Eye className="h-5 w-5 text-neutral-400" />
          <h2 className="text-lg font-semibold text-neutral-900">Preview Notification Emails</h2>
        </div>
        <p className="text-sm text-neutral-600 mb-4">
          Send real notification emails using actual hold request data. Useful for testing email templates and CTA links.
        </p>

        <div className="space-y-4">
          <div>
            <label htmlFor="preview-hold-id" className="block text-sm font-medium text-neutral-600 mb-1">
              Hold Request ID or Number
            </label>
            <input
              id="preview-hold-id"
              type="text"
              value={previewHoldRequestId}
              onChange={(e) => setPreviewHoldRequestId(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-outfront-purple focus:border-outfront-purple"
              placeholder="e.g. HR26000047 or UUID"
            />
          </div>

          <div>
            <label htmlFor="preview-type" className="block text-sm font-medium text-neutral-600 mb-1">
              Notification Type
            </label>
            <select
              id="preview-type"
              value={previewNotificationType}
              onChange={(e) => setPreviewNotificationType(e.target.value as typeof previewNotificationType)}
              className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-outfront-purple focus:border-outfront-purple"
            >
              <option value="hold_request_created">Hold Request Created</option>
              <option value="hold_request_completed">Hold Request Completed</option>
              <option value="line_item_assigned">Line Item Assigned (Charting)</option>
              <option value="line_item_status_changed">Line Item Status Changed (Charting)</option>
              <option value="hold_request_overdue">Hold Request Overdue (Charting)</option>
              <option value="exception_approval_required">Exception Approval Required (Charting)</option>
            </select>
          </div>

          <div>
            <label htmlFor="preview-email" className="block text-sm font-medium text-neutral-600 mb-1">
              Recipient Email
            </label>
            <input
              id="preview-email"
              type="email"
              value={previewRecipientEmail}
              onChange={(e) => setPreviewRecipientEmail(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-outfront-purple focus:border-outfront-purple"
              placeholder="email@outfront.com"
            />
          </div>

          <Button
            onClick={handlePreviewEmail}
            disabled={previewEmailMutation.isPending || !previewHoldRequestId || !previewRecipientEmail}
          >
            {previewEmailMutation.isPending ? (
              <>
                <LoadingSpinner />
                <span className="ml-2">Sending...</span>
              </>
            ) : (
              <>
                <Eye className="h-4 w-4 mr-2" />
                Send Preview Email
              </>
            )}
          </Button>
        </div>

        {previewResult && (
          <div className={`mt-4 p-4 rounded-lg ${previewResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-start gap-2">
              {previewResult.success ? (
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className={`text-sm font-medium ${previewResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {previewResult.message}
                </p>
                {previewResult.success && previewResult.data && (
                  <div className="mt-2 text-sm text-green-700">
                    <p>Subject: {previewResult.data.subject}</p>
                    <p>Request: {previewResult.data.requestNumber}</p>
                    <p>Recipient: {previewResult.data.recipient}</p>
                    {previewResult.data.marketDLRouting && (
                      <div className="mt-2 pt-2 border-t border-green-200">
                        <p className="font-medium">Market DL Routing (Charting Type):</p>
                        <p>Markets: {previewResult.data.marketDLRouting.marketCodes.join(', ') || 'None found'}</p>
                        <p>DL Addresses: {previewResult.data.marketDLRouting.distributionListAddresses.join(', ') || 'None resolved'}</p>
                        <p className="text-xs text-green-600 mt-1 italic">{previewResult.data.marketDLRouting.note}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Service API Key Management */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Key className="h-5 w-5 text-neutral-400" />
          <h2 className="text-lg font-semibold text-neutral-900">Service API Key</h2>
        </div>
        <p className="text-sm text-neutral-600 mb-4">
          Manage the service API key used by OUTGPT to authenticate hold request submissions on behalf of users.
        </p>

        {serviceApiKeyStatusLoading ? (
          <div className="flex items-center gap-2 text-sm text-neutral-400 mb-4">
            <LoadingSpinner />
            <span>Loading service API key status...</span>
          </div>
        ) : serviceApiKeyStatus ? (
          <div className="mb-4">
            {serviceApiKeyStatus.configured ? (
              <div className="text-sm bg-green-50 text-green-700 p-3 rounded-lg">
                <p className="font-medium">Configured</p>
                {serviceApiKeyStatus.source === 'env' ? (
                  <p className="text-xs mt-1">Configured via environment (legacy)</p>
                ) : (
                  <>
                    <p className="mt-1 font-mono">
                      {serviceApiKeyStatus.prefix}••••{serviceApiKeyStatus.lastFour}
                    </p>
                    <p className="text-xs mt-1">
                      Created {serviceApiKeyStatus.createdAt ? new Date(serviceApiKeyStatus.createdAt).toLocaleString() : 'Unknown'}
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="text-sm bg-yellow-50 text-yellow-700 p-3 rounded-lg">
                <p className="font-medium">Not configured</p>
              </div>
            )}
          </div>
        ) : null}

        {generatedApiKey && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm font-medium text-yellow-800 mb-2">
              Copy this now. It will not be shown again.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={generatedApiKey}
                readOnly
                aria-label="Generated service API key"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                data-1p-ignore
                className="flex-1 px-3 py-2 border border-yellow-300 rounded-md bg-white font-mono text-sm"
              />
              <Button onClick={handleCopyApiKey} variant="outline" size="sm">
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-1" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            onClick={handleGenerateServiceApiKey}
            disabled={generateServiceApiKeyMutation.isPending || serviceApiKeyStatusLoading}
          >
            {generateServiceApiKeyMutation.isPending ? (
              <>
                <LoadingSpinner />
                <span className="ml-2">Generating...</span>
              </>
            ) : (
              <>
                <Key className="h-4 w-4 mr-2" />
                {serviceApiKeyStatus?.configured ? 'Regenerate Key' : 'Generate Key'}
              </>
            )}
          </Button>

          {serviceApiKeyStatus?.configured && serviceApiKeyStatus.source !== 'env' && (
            <Button
              onClick={handleRevokeServiceApiKey}
              disabled={revokeServiceApiKeyMutation.isPending || serviceApiKeyStatusLoading}
              variant="outline"
            >
              {revokeServiceApiKeyMutation.isPending ? (
                <>
                  <LoadingSpinner />
                  <span className="ml-2">Revoking...</span>
                </>
              ) : (
                'Revoke'
              )}
            </Button>
          )}
        </div>
      </Card>

      {/* Expire Holds Tool */}
      <Card className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-5 w-5 text-neutral-400" />
              <h2 className="text-lg font-semibold text-neutral-900">Expire Holds</h2>
            </div>
            <p className="text-sm text-neutral-600 mb-4">
              Manually trigger the hold expiration job. This will update line items and hold requests
              that have passed their expiration dates. Normally runs automatically at midnight EST.
            </p>
            <Button
              onClick={handleExpireHolds}
              disabled={expireHoldsMutation.isPending}
            >
              {expireHoldsMutation.isPending ? (
                <>
                  <LoadingSpinner />
                  <span className="ml-2">Running...</span>
                </>
              ) : (
                'Run Expire Holds'
              )}
            </Button>
          </div>
        </div>

        {/* Expire Result Display */}
        {expireResult && (
          <div
            className={`mt-4 p-4 rounded-lg ${
              expireResult.success
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <div className="flex items-start gap-2">
              {expireResult.success ? (
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p
                  className={`text-sm font-medium ${
                    expireResult.success ? 'text-green-800' : 'text-red-800'
                  }`}
                >
                  {expireResult.message}
                </p>
                {expireResult.success && expireResult.data && (
                  <div className="mt-2 text-sm text-green-700">
                    <p>Line items expired: {expireResult.data.expiredLineItems}</p>
                    <p className="text-xs text-green-600 mt-1">
                      Completed at {new Date(expireResult.data.timestamp).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
